import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWarning } from "@fortawesome/sharp-light-svg-icons";
import { useAtom } from "jotai";
import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { ExpansionProvider } from "../../ui/ExpandableArea";
import { AggregatedQuarterData } from "./AggregatedQuarterGlobal";
import { AvgPerSite } from "./AvgPerSite";
import { DetectionLimitFilter } from "./Filters/DetectionLimitFilter";
import { LocationPresetFilterWrapper } from "./Filters/LocationPresetFilterWrapper";
import { ProviderFilterWrapper } from "./Filters/ProviderFilterWrapper";
import { QuarterPicker } from "./Filters/QuarterPicker";
import { SiteTypeFilterWrapper } from "./Filters/SiteTypeFilterWrapper";
import { GroupedBySiteType } from "./GroupedBySiteType";
import { QuarterProvider } from "./PerProvider";
import { ShareControl } from "./ShareControl";
import {
    analyticsFiltersAtom,
    defaultAnalyticsFilters,
    isDefaultFilters,
} from "./state";
import { TotalPerSite } from "./TotalPerSite";
import { urlParamsToFilters } from "./utils/urlParams";

export const AnalyticsView = () => {
    // Retrieve search params
    const [urlParams, setSearchParams] = useSearchParams();

    // Use the global analytics filters atom
    const [filters, setFilters] = useAtom(analyticsFiltersAtom);

    // Parse URL params into filters and apply them on mount
    useEffect(() => {
        const urlFilters = urlParamsToFilters(urlParams);
        const hasAnalyticsParams =
            urlParams.has("quarterFrom") ||
            urlParams.has("quarterTo") ||
            urlParams.has("providers") ||
            urlParams.has("detectionLimit") ||
            urlParams.has("locationPreset") ||
            urlParams.has("siteType");

        // Apply URL params to atom if present
        if (hasAnalyticsParams && Object.keys(urlFilters).length > 0) {
            setFilters((current) => ({ ...current, ...urlFilters }));
            // Clear URL parameters after applying
            setSearchParams(new URLSearchParams());
        }
    }, []); // Only run on mount

    return (
        <div className="h-screen w-full flex overflow-y-auto">
            <div className="flex-1 px-4 pt-4 pb-6 flex flex-col min-h-full">
                <div className="flex justify-between mt-1 mb-2">
                    <h1 className="ml-1 text-2xl flex items-center gap-2">
                        Analytics
                        <div className="bg-yellow-400 text-xs rounded px-1 py-0.5 text-yellow-800 flex items-center gap-1">
                            <FontAwesomeIcon icon={faWarning} />
                            Experimental
                        </div>
                    </h1>
                    {/* Date controls and settings */}
                    <div className="flex items-center justify-end gap-2">
                        {!isDefaultFilters(filters) && (
                            <button
                                type="button"
                                title="Clear filters"
                                onClick={() =>
                                    setFilters(defaultAnalyticsFilters)
                                }
                                className="flex items-center gap-2 px-2 h-8 text-xs text-ae-blue-550 hover:bg-neutral-200 rounded whitespace-nowrap"
                            >
                                Clear all
                            </button>
                        )}
                        <DetectionLimitFilter
                            value={filters.detectionLimit}
                            onChange={(d) =>
                                setFilters((f) => ({ ...f, detectionLimit: d }))
                            }
                        />
                        <QuarterPicker
                            startQuarter={filters.quarterFrom}
                            onChangeStartQuarter={(q) =>
                                setFilters((f) => ({ ...f, quarterFrom: q }))
                            }
                            endQuarter={filters.quarterTo}
                            onChangeEndQuarter={(q) =>
                                setFilters((f) => ({ ...f, quarterTo: q }))
                            }
                        />
                        <LocationPresetFilterWrapper
                            filterValue={filters.locationPreset}
                            setFilterValue={(value) =>
                                setFilters((f) => ({
                                    ...f,
                                    locationPreset: value,
                                }))
                            }
                        />
                        <SiteTypeFilterWrapper
                            filterValue={filters.siteType}
                            setFilterValue={(value) =>
                                setFilters((f) => ({ ...f, siteType: value }))
                            }
                        />
                        <ProviderFilterWrapper
                            filterValue={filters.providers}
                            setFilterValue={(p) =>
                                setFilters((f) => ({ ...f, providers: p }))
                            }
                        />
                        <ShareControl />
                    </div>
                </div>

                {/* Page content */}
                <div className="flex-1">
                    <ExpansionProvider gridClassName="grid grid-cols-1 lg:grid-cols-2  gap-2 auto-rows-[calc(50vh-4rem)]">
                        <AggregatedQuarterData filters={filters} />
                        <GroupedBySiteType filters={filters} />
                        <TotalPerSite filters={filters} />
                        <QuarterProvider filters={filters} />
                        <AvgPerSite filters={filters} />
                    </ExpansionProvider>
                </div>
            </div>
        </div>
    );
};
